import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Eye,
  EyeOff,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { useConfirm } from "@/contexts/ConfirmContext";
import { useGlobalToast } from "@/contexts/ToastContext";

/**
 * SUPERADMIN → Clientes → Visibilidad en la app (sep-2026).
 *
 * `Location.showInApp` nace en `false` (opt-in) desde que salir en la app de
 * clientes finales dejó de ser automático. Esta pantalla deja al superadmin
 * activar/desactivar la visibilidad de cada sede y, sobre todo, hacerlo EN
 * BLOQUE agrupando por si la organización tiene la suscripción al corriente
 * ("Activas": pagando o en prueba) o no ("Inactivas": todo lo demás,
 * incluido el plan Free — es un cliente real que no paga, no una alarma).
 */

type OrganizacionEstado =
  | "pagando"
  | "prueba"
  | "gratis"
  | "prueba_caducada"
  | "impagada"
  | "cancelada"
  | "suspendida"
  | "otro";

interface FilaVisibilidad {
  locationId: string;
  nombre: string;
  ciudad: string;
  showInApp: boolean;
  listoParaDirectorio: boolean;
  motivoNoListo: string | null;
  organizacion: {
    id: string;
    nombre: string;
    plan: string;
    planPrecio: number;
    estado: OrganizacionEstado;
    trialEndsAt: string | null;
  };
}

interface Resumen {
  activas: number;
  inactivas: number;
  visiblesActivas: number;
  visiblesInactivas: number;
}

interface AppVisibilityData {
  activas: FilaVisibilidad[];
  inactivas: FilaVisibilidad[];
  resumen: Resumen;
}

type Grupo = "activas" | "inactivas";

const ESTADO_LABEL: Record<OrganizacionEstado, string> = {
  pagando: "Pagando",
  prueba: "Prueba",
  gratis: "Gratis",
  prueba_caducada: "Prueba caducada",
  impagada: "Impagada",
  cancelada: "Cancelada",
  suspendida: "Suspendida",
  otro: "Otro",
};

const ESTADO_STYLE: Record<OrganizacionEstado, string> = {
  pagando: "bg-green-100 text-green-800",
  prueba: "bg-blue-100 text-blue-800",
  gratis: "bg-gray-100 text-gray-600",
  prueba_caducada: "bg-amber-100 text-amber-800",
  impagada: "bg-amber-100 text-amber-800",
  cancelada: "bg-red-50 text-red-700",
  suspendida: "bg-red-50 text-red-700",
  otro: "bg-gray-100 text-gray-600",
};

function EstadoBadge({ estado }: { estado: OrganizacionEstado }) {
  return (
    <span
      className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${ESTADO_STYLE[estado] ?? ESTADO_STYLE.otro}`}
    >
      {ESTADO_LABEL[estado] ?? estado}
    </span>
  );
}

function formatTrialEnd(dateString: string | null): string | null {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
}

function coincide(fila: FilaVisibilidad, term: string): boolean {
  if (!term) return true;
  const t = term.toLowerCase();
  return (
    fila.nombre.toLowerCase().includes(t) ||
    fila.organizacion.nombre.toLowerCase().includes(t) ||
    fila.ciudad.toLowerCase().includes(t)
  );
}

interface FilaProps {
  fila: FilaVisibilidad;
  grupo: Grupo;
  disabled: boolean;
  onToggle: (fila: FilaVisibilidad, grupo: Grupo) => void;
}

function Fila({ fila, grupo, disabled, onToggle }: FilaProps) {
  const trialEnd =
    fila.organizacion.estado === "prueba"
      ? formatTrialEnd(fila.organizacion.trialEndsAt)
      : null;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {fila.nombre}
        </p>
        <p className="text-xs text-gray-500 truncate">
          {fila.organizacion.nombre} · {fila.ciudad}
        </p>
        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
          <EstadoBadge estado={fila.organizacion.estado} />
          {trialEnd && (
            <span className="text-xs text-gray-400">hasta {trialEnd}</span>
          )}
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">
            {fila.organizacion.plan} · {fila.organizacion.planPrecio} €/mes
          </span>
        </div>
        {!fila.listoParaDirectorio && (
          <p className="flex items-start gap-1.5 text-xs text-amber-700 mt-1.5">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <span>
              {fila.motivoNoListo ?? "Datos incompletos"}: aunque lo marques
              visible, no aparecerá en la app hasta que complete ese dato.
            </span>
          </p>
        )}
      </div>
      <button
        onClick={() => onToggle(fila, grupo)}
        disabled={disabled}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none flex-shrink-0 self-start sm:self-center ${
          fila.showInApp ? "bg-primary-600" : "bg-gray-300"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        title={fila.showInApp ? "Ocultar en la app" : "Mostrar en la app"}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
            fila.showInApp ? "translate-x-4.5" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}

interface SeccionProps {
  titulo: string;
  descripcion: string;
  grupo: Grupo;
  filas: FilaVisibilidad[];
  total: number;
  visibles: number;
  togglingId: string | null;
  bulkLoading: { grupo: Grupo; visible: boolean } | null;
  onToggle: (fila: FilaVisibilidad, grupo: Grupo) => void;
  onBulk: (grupo: Grupo, visible: boolean) => void;
}

function Seccion({
  titulo,
  descripcion,
  grupo,
  filas,
  total,
  visibles,
  togglingId,
  bulkLoading,
  onToggle,
  onBulk,
}: SeccionProps) {
  const bulkBusy = bulkLoading !== null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{titulo}</h2>
          <p className="text-xs text-gray-500 mt-0.5">{descripcion}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {total} negocio{total === 1 ? "" : "s"} · {visibles} visible
            {visibles === 1 ? "" : "s"} en la app
          </p>
        </div>
        <div className="flex gap-2 flex-wrap flex-shrink-0">
          <button
            onClick={() => onBulk(grupo, true)}
            disabled={total === 0 || bulkBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {bulkLoading?.grupo === grupo && bulkLoading.visible ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Eye className="h-3.5 w-3.5" />
            )}
            Mostrar todas
          </button>
          <button
            onClick={() => onBulk(grupo, false)}
            disabled={total === 0 || bulkBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {bulkLoading?.grupo === grupo && !bulkLoading.visible ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <EyeOff className="h-3.5 w-3.5" />
            )}
            Ocultar todas
          </button>
        </div>
      </div>

      {total === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-gray-400">
          No hay negocios en este grupo.
        </p>
      ) : filas.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-gray-400">
          Ninguno coincide con la búsqueda.
        </p>
      ) : (
        <div>
          {filas.map((fila) => (
            <Fila
              key={fila.locationId}
              fila={fila}
              grupo={grupo}
              disabled={togglingId === fila.locationId || bulkBusy}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppVisibility() {
  const { confirm } = useConfirm();
  const { showToast } = useGlobalToast();

  const [data, setData] = useState<AppVisibilityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [bulkLoading, setBulkLoading] = useState<{
    grupo: Grupo;
    visible: boolean;
  } | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: res } = await apiClient.get<{
        success: boolean;
        data: AppVisibilityData;
      }>("/admin/app-visibility");
      setData(res.data);
    } catch (err) {
      setError(
        extractApiErrorMessage(
          err,
          "No se pudo cargar la visibilidad en la app",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleToggle = async (fila: FilaVisibilidad, grupo: Grupo) => {
    const nuevoValor = !fila.showInApp;
    setTogglingId(fila.locationId);
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [grupo]: prev[grupo].map((f) =>
          f.locationId === fila.locationId
            ? { ...f, showInApp: nuevoValor }
            : f,
        ),
      };
    });
    try {
      await apiClient.patch<{ success: boolean; data: { showInApp: boolean } }>(
        `/admin/locations/${fila.locationId}/show-in-app`,
        { visible: nuevoValor },
      );
      showToast(
        nuevoValor
          ? `"${fila.nombre}" ahora es visible en la app`
          : `"${fila.nombre}" ya no aparece en la app`,
        "success",
      );
    } catch (err) {
      // Revertir el estado optimista
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          [grupo]: prev[grupo].map((f) =>
            f.locationId === fila.locationId
              ? { ...f, showInApp: !nuevoValor }
              : f,
          ),
        };
      });
      showToast(
        extractApiErrorMessage(err, "No se pudo actualizar la visibilidad"),
        "error",
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleBulk = async (grupo: Grupo, visible: boolean) => {
    if (!data) return;
    const total = data[grupo].length;
    if (total === 0) return;

    const accion = visible ? "mostrar" : "ocultar";
    const confirmed = await confirm({
      title: visible ? "Mostrar todas en la app" : "Ocultar todas de la app",
      message: `Vas a ${accion} ${total} negocio${total === 1 ? "" : "s"} de la app. ¿Seguro?`,
      confirmText: visible ? "Sí, mostrar todas" : "Sí, ocultar todas",
      danger: !visible,
    });
    if (!confirmed) return;

    setBulkLoading({ grupo, visible });
    try {
      const { data: res } = await apiClient.post<{
        success: boolean;
        data: { cambiadas: number };
      }>("/admin/app-visibility/bulk", { grupo, visible });
      const cambiadas = res.data.cambiadas;
      showToast(
        `${cambiadas} negocio${cambiadas === 1 ? "" : "s"} actualizado${cambiadas === 1 ? "" : "s"}`,
        "success",
      );
      await load();
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo aplicar la acción en bloque"),
        "error",
      );
    } finally {
      setBulkLoading(null);
    }
  };

  const activasFiltradas = useMemo(
    () => (data ? data.activas.filter((f) => coincide(f, search)) : []),
    [data, search],
  );
  const inactivasFiltradas = useMemo(
    () => (data ? data.inactivas.filter((f) => coincide(f, search)) : []),
    [data, search],
  );

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Eye className="w-6 h-6 text-primary-600" /> Visibilidad en la
            app
          </h1>
          <p className="text-gray-500 text-sm mt-1 max-w-2xl">
            Activas: suscripción de pago al corriente o dentro del periodo de
            prueba. Inactivas: todo lo demás (Free, prueba caducada, impago,
            cancelada o suspendida).
          </p>
        </div>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </button>
      </div>

      {data && (
        <div className="relative max-w-md">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
          />
          <input
            type="text"
            placeholder="Buscar por negocio o sede..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      )}

      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 text-sm text-red-700">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            onClick={() => void load()}
            className="underline flex-shrink-0"
          >
            Reintentar
          </button>
        </div>
      )}

      {data && (
        <div className="space-y-4 md:space-y-6">
          <Seccion
            titulo="Activas"
            descripcion="Pagando o dentro del periodo de prueba."
            grupo="activas"
            filas={activasFiltradas}
            total={data.activas.length}
            visibles={data.resumen.visiblesActivas}
            togglingId={togglingId}
            bulkLoading={bulkLoading}
            onToggle={handleToggle}
            onBulk={handleBulk}
          />
          <Seccion
            titulo="Inactivas"
            descripcion="Free, prueba caducada, impago, cancelada o suspendida. Marcarlas no las hace aparecer: el directorio de la app solo admite planes de pago y pruebas vigentes."
            grupo="inactivas"
            filas={inactivasFiltradas}
            total={data.inactivas.length}
            visibles={data.resumen.visiblesInactivas}
            togglingId={togglingId}
            bulkLoading={bulkLoading}
            onToggle={handleToggle}
            onBulk={handleBulk}
          />
        </div>
      )}
    </div>
  );
}
